import { doc, getDoc } from "firebase/firestore";
import { dbFirebase } from "../../Conections/Firebase";
import { fetchWhere } from "../../Utils/firestore";
import { useParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PaidIcon from "@mui/icons-material/Paid";
import SavingsIcon from "@mui/icons-material/Savings";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import { formatearNumero } from "../../Utils/formatUtils";
import { normalizarFecha } from "../../Utils/dateUtils";
import { useThemeMode } from "../../Utils/ThemeModeContext";

const summaryCardSx = (theme) => ({
  height: "100%",
  borderRadius: 4,
  boxShadow:
    theme.palette.mode === "dark"
      ? "0 18px 40px rgba(0, 0, 0, 0.45)"
      : "0 18px 40px rgba(16, 24, 40, 0.08)",
  border: `1px solid ${theme.palette.divider}`,
});

const ConsultaDeudor = () => {
  const { token } = useParams();
  const theme = useTheme();
  const { mode, toggleMode } = useThemeMode();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comprobanteSeleccionado, setComprobanteSeleccionado] = useState(null);
  const [expirado, setExpirado] = useState(false);
  const [contador, setContador] = useState(5);

  useEffect(() => {
    const cargarDatos = async () => {
      try {
        const tokenDoc = await getDoc(doc(dbFirebase, "tokensTemporales", token));
        if (!tokenDoc.exists()) {
          setExpirado(true);
          return;
        }

        const { deudorId, expiresAt, numeroPrestamo } = tokenDoc.data();
        const ahora = new Date();
        if (expiresAt.toDate() < ahora) {
          setExpirado(true);
          return;
        }

        const deudorSnap = await getDoc(doc(dbFirebase, "deudores", deudorId));
        const [prestamosData, comprobantes] = await Promise.all([
          fetchWhere("prestamos", "deudor", "==", deudorId),
          fetchWhere("comprobantes", "deudorId", "==", deudorId),
        ]);

        const prestamos = prestamosData.map((datos) => ({
          ...datos,
          fechaRaw: datos.fecha,
          fecha: normalizarFecha(datos.fecha),
        }));

        const deudorData = deudorSnap.data();

        const prestamosFiltrados = prestamos.filter((pago) => {
          if (numeroPrestamo) {
            // Incluir pagos que tengan ese numeroPrestamo específico
            // O pagos sin numeroPrestamo (pagos directos del deudor)
            return !pago.numeroPrestamo || pago.numeroPrestamo === numeroPrestamo;
          }

          return true;
        });

        const prestamosMostrados =
          prestamosFiltrados.length > 0 ? prestamosFiltrados : prestamos;

        const restante =
          Number(deudorData.totalDeuda || 0) -
          prestamosMostrados.reduce((acc, docPres) => {
            return acc + (Number(docPres.cantidad) || 0);
          }, 0);

        const prestamosConComprobante = prestamosMostrados
          .map((p) => {
            const comprobante = comprobantes.find((c) => c.id === p.comprobanteId);
            return { ...p, comprobante };
          })
          .sort(
            (a, b) =>
              new Date(b.fechaRaw || b.fecha || 0).getTime() -
              new Date(a.fechaRaw || a.fecha || 0).getTime()
          );

        setData({
          deudor: deudorData,
          restante: Math.max(0, restante),
          prestamos: prestamosConComprobante,
          numeroPrestamo: numeroPrestamo || deudorData.numeroPrestamoActual || 1,
        });
      } catch (err) {
        console.error(err);
        setExpirado(true);
      } finally {
        setLoading(false);
      }
    };

    cargarDatos();
  }, [token]);

  useEffect(() => {
    if (expirado) {
      const interval = setInterval(() => {
        setContador((prev) => {
          if (prev === 1) {
            window.location.href = "https://www.google.com";
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [expirado]);

  const metrics = useMemo(() => {
    if (!data) {
      return null;
    }

    const totalDeuda = Number(data.deudor.totalDeuda || 0);
    const totalAbonado = data.prestamos.reduce(
      (acc, item) => acc + (Number(item.cantidad) || 0),
      0
    );
    const cantidadPagos = data.prestamos.length;
    const ultimoPago = data.prestamos[0] || null;
    const primerPago = data.prestamos[data.prestamos.length - 1] || null;
    const promedioPago = cantidadPagos > 0 ? totalAbonado / cantidadPagos : 0;
    const progreso = totalDeuda > 0 ? Math.min((totalAbonado / totalDeuda) * 100, 100) : 0;
    const estado =
      data.restante <= 0
        ? "Liquidado"
        : progreso >= 70
        ? "Avance alto"
        : progreso >= 35
        ? "En progreso"
        : "Inicio de pago";

    return {
      totalDeuda,
      totalAbonado,
      cantidadPagos,
      ultimoPago,
      primerPago,
      promedioPago,
      progreso,
      estado,
    };
  }, [data]);

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: isDark
            ? "linear-gradient(160deg, #0b1120 0%, #0f172a 40%, #0b1120 100%)"
            : "linear-gradient(160deg, #f3f9f5 0%, #ffffff 40%, #eef6ff 100%)",
        }}
      >
        <Box sx={{ textAlign: "center" }}>
          <CircularProgress sx={{ color: isDark ? "#5eead4" : "#147a5c" }} />
          <Typography sx={{ mt: 2, color: "text.secondary" }}>
            Cargando tu estado de cuenta...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (expirado) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: isDark
            ? "linear-gradient(160deg, #1f0f0b 0%, #0f172a 55%, #1a0f0b 100%)"
            : "linear-gradient(160deg, #fff5f2 0%, #ffffff 55%, #fff0ea 100%)",
          p: 3,
        }}
      >
        <Card sx={{ maxWidth: 520, width: "100%", borderRadius: 5 }}>
          <CardContent sx={{ p: 4, textAlign: "center" }}>
            <Typography variant="h4" color="error" gutterBottom>
              Enlace expirado
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Este enlace ya no está disponible. La página se cerrará automáticamente en{" "}
              {contador} segundos.
            </Typography>
            <CircularProgress color="error" sx={{ mt: 3 }} />
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (!data || !metrics) {
    return null;
  }

  const { deudor, prestamos } = data;
  const initials = (deudor.nombre || "D")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((item) => item[0]?.toUpperCase())
    .join("");

  const summaryCards = [
    {
      label: "Total del préstamo",
      value: formatearNumero(metrics.totalDeuda),
      helper: `Préstamo #${data.numeroPrestamo}`,
      icon: <ReceiptLongIcon />,
      color: isDark ? "#5eead4" : "#0f766e",
    },
    {
      label: "Total abonado",
      value: formatearNumero(metrics.totalAbonado),
      helper: `${metrics.cantidadPagos} pagos registrados`,
      icon: <PaidIcon />,
      color: isDark ? "#4ade80" : "#15803d",
    },
    {
      label: "Saldo pendiente",
      value: formatearNumero(data.restante),
      helper: metrics.estado,
      icon: <SavingsIcon />,
      color: isDark ? "#fbbf24" : "#b45309",
    },
    {
      label: "Promedio por abono",
      value: formatearNumero(metrics.promedioPago),
      helper: metrics.ultimoPago ? `Último: ${metrics.ultimoPago.fecha}` : "Sin pagos aún",
      icon: <TrendingUpIcon />,
      color: isDark ? "#60a5fa" : "#1d4ed8",
    },
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        py: { xs: 3, md: 5 },
        background: isDark
          ? "radial-gradient(circle at top left, rgba(20,122,92,0.20), transparent 30%), radial-gradient(circle at top right, rgba(29,78,216,0.16), transparent 26%), linear-gradient(180deg, #0b1120 0%, #0f172a 100%)"
          : "radial-gradient(circle at top left, rgba(20,122,92,0.12), transparent 28%), radial-gradient(circle at top right, rgba(29,78,216,0.08), transparent 24%), linear-gradient(180deg, #f7fbf8 0%, #ffffff 100%)",
      }}
    >
      <Container maxWidth="lg">
        <Card
          sx={{
            borderRadius: 6,
            overflow: "hidden",
            boxShadow: isDark
              ? "0 24px 70px rgba(0, 0, 0, 0.55)"
              : "0 24px 70px rgba(15, 23, 42, 0.10)",
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box
            sx={{
              px: { xs: 3, md: 5 },
              py: { xs: 4, md: 5 },
              background:
                "linear-gradient(135deg, #0f766e 0%, #147a5c 40%, #1d4ed8 100%)",
              color: "white",
              position: "relative",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                background:
                  "linear-gradient(120deg, rgba(255,255,255,0.14), transparent 32%, transparent 68%, rgba(255,255,255,0.10))",
                pointerEvents: "none",
              }}
            />
            <Box
              sx={{
                position: "relative",
                display: "flex",
                justifyContent: "flex-end",
                mb: 2,
              }}
            >
              <IconButton
                onClick={toggleMode}
                aria-label="Cambiar tema"
                sx={{
                  color: "white",
                  bgcolor: "rgba(255,255,255,0.14)",
                  border: "1px solid rgba(255,255,255,0.22)",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.24)" },
                }}
              >
                {mode === "dark" ? <LightModeIcon /> : <DarkModeIcon />}
              </IconButton>
            </Box>
            <Grid container spacing={3} alignItems="center" sx={{ position: "relative" }}>
              <Grid item xs={12} md={8}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                  <Avatar
                    sx={{
                      width: 64,
                      height: 64,
                      bgcolor: "rgba(255,255,255,0.16)",
                      border: "1px solid rgba(255,255,255,0.22)",
                      fontSize: 24,
                      fontWeight: 700,
                    }}
                  >
                    {initials}
                  </Avatar>
                  <Box>
                    <Typography
                      variant="overline"
                      sx={{ letterSpacing: 1.8, opacity: 0.85, display: "block" }}
                    >
                      Tu estado de cuenta
                    </Typography>
                    <Typography variant="h3" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                      {deudor.nombre}
                    </Typography>
                  </Box>
                </Box>

                <Typography
                  variant="body1"
                  sx={{ maxWidth: 620, opacity: 0.92, fontSize: { xs: 15, md: 17 } }}
                >
                  Aquí puedes consultar el avance de tu préstamo, tu saldo pendiente y el
                  historial de abonos registrados.
                </Typography>
              </Grid>

              <Grid item xs={12} md={4}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    borderRadius: 4,
                    backgroundColor: "rgba(255,255,255,0.14)",
                    border: "1px solid rgba(255,255,255,0.2)",
                    color: "white",
                    backdropFilter: "blur(8px)",
                  }}
                >
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Estado actual
                  </Typography>
                  <Chip
                    label={metrics.estado}
                    sx={{
                      mt: 1,
                      mb: 2,
                      bgcolor: "rgba(255,255,255,0.18)",
                      color: "white",
                      borderRadius: 2,
                    }}
                  />
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Avance de pago
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {metrics.progreso.toFixed(1)}%
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={metrics.progreso}
                    sx={{
                      mt: 2,
                      height: 10,
                      borderRadius: 999,
                      bgcolor: "rgba(255,255,255,0.22)",
                      "& .MuiLinearProgress-bar": {
                        borderRadius: 999,
                        bgcolor: "#facc15",
                      },
                    }}
                  />
                </Paper>
              </Grid>
            </Grid>
          </Box>

          <Box sx={{ p: { xs: 3, md: 4 } }}>
            <Grid container spacing={2.5}>
              {summaryCards.map((item) => (
                <Grid item xs={12} sm={6} md={3} key={item.label}>
                  <Card
                    sx={{
                      ...summaryCardSx(theme),
                      backgroundColor: alpha(item.color, isDark ? 0.16 : 0.08),
                    }}
                  >
                    <CardContent sx={{ p: 2.5 }}>
                      <Box
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: 3,
                          display: "grid",
                          placeItems: "center",
                          mb: 2,
                          color: item.color,
                          bgcolor: isDark ? alpha(item.color, 0.22) : "rgba(255,255,255,0.72)",
                        }}
                      >
                        {item.icon}
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {item.label}
                      </Typography>
                      <Typography
                        variant="h5"
                        sx={{ mt: 0.5, fontWeight: 700, color: "text.primary" }}
                      >
                        {item.value}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
                        {item.helper}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}

              <Grid item xs={12} md={8}>
                <Card sx={{ ...summaryCardSx(theme), borderRadius: 5 }}>
                  <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Resumen del préstamo
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Visualiza rápidamente cuánto has pagado y cuánto falta por liquidar.
                    </Typography>

                    <Box sx={{ mt: 3 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 2,
                          flexWrap: "wrap",
                          mb: 1.5,
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Progreso acumulado
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {formatearNumero(metrics.totalAbonado)} de{" "}
                          {formatearNumero(metrics.totalDeuda)}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={metrics.progreso}
                        sx={{
                          height: 14,
                          borderRadius: 999,
                          bgcolor: isDark ? alpha("#16a34a", 0.2) : "#e5efe8",
                          "& .MuiLinearProgress-bar": {
                            borderRadius: 999,
                            background:
                              "linear-gradient(90deg, #15803d 0%, #16a34a 50%, #65a30d 100%)",
                          },
                        }}
                      />
                    </Box>

                    <Grid container spacing={2} sx={{ mt: 1.5 }}>
                      <Grid item xs={12} sm={6}>
                        <Paper
                          variant="outlined"
                          sx={{ p: 2, borderRadius: 3, borderColor: theme.palette.divider }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            Último pago
                          </Typography>
                          <Typography variant="h6" sx={{ mt: 0.5, fontWeight: 700 }}>
                            {metrics.ultimoPago
                              ? formatearNumero(metrics.ultimoPago.cantidad)
                              : "Sin registros"}
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            {metrics.ultimoPago?.fecha || "Todavía no hay pagos registrados."}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Paper
                          variant="outlined"
                          sx={{ p: 2, borderRadius: 3, borderColor: theme.palette.divider }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            Primer pago registrado
                          </Typography>
                          <Typography variant="h6" sx={{ mt: 0.5, fontWeight: 700 }}>
                            {metrics.primerPago
                              ? formatearNumero(metrics.primerPago.cantidad)
                              : "Sin registros"}
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            {metrics.primerPago?.fecha || "Todavía no hay pagos registrados."}
                          </Typography>
                        </Paper>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card sx={{ ...summaryCardSx(theme), borderRadius: 5 }}>
                  <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Datos rápidos
                    </Typography>

                    <Box sx={{ mt: 2.5, display: "grid", gap: 2 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <CalendarMonthIcon sx={{ color: isDark ? "#5eead4" : "#147a5c" }} />
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Número de pagos
                          </Typography>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            {metrics.cantidadPagos}
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <PaidIcon sx={{ color: isDark ? "#60a5fa" : "#1d4ed8" }} />
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Pago promedio
                          </Typography>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            {formatearNumero(metrics.promedioPago)}
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <SavingsIcon sx={{ color: isDark ? "#fbbf24" : "#b45309" }} />
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Saldo restante
                          </Typography>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            {formatearNumero(data.restante)}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card sx={{ ...summaryCardSx(theme), borderRadius: 5 }}>
                  <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Historial de abonos
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 3 }}>
                      Los pagos se muestran del más reciente al más antiguo.
                    </Typography>

                    {prestamos.length === 0 ? (
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 3,
                          borderRadius: 4,
                          textAlign: "center",
                          borderStyle: "dashed",
                        }}
                      >
                        <Typography variant="body1">No hay pagos registrados.</Typography>
                      </Paper>
                    ) : isMobile ? (
                      <Box sx={{ display: "grid", gap: 2 }}>
                        {prestamos.map((item, index) => (
                          <Paper
                            key={item.id}
                            variant="outlined"
                            sx={{
                              p: 2,
                              borderRadius: 3,
                              borderColor: theme.palette.divider,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                gap: 2,
                                alignItems: "flex-start",
                              }}
                            >
                              <Box>
                                <Typography variant="body2" color="text.secondary">
                                  Abono #{prestamos.length - index}
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.5 }}>
                                  {formatearNumero(item.cantidad)}
                                </Typography>
                              </Box>
                              <IconButton
                                onClick={() => setComprobanteSeleccionado(item.comprobante?.base64)}
                                disabled={!item.comprobante}
                              >
                                <VisibilityIcon />
                              </IconButton>
                            </Box>

                            <Typography variant="body2" sx={{ mt: 1.5 }}>
                              {item.fecha}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              {item.descripcion || "Sin descripción"}
                            </Typography>
                          </Paper>
                        ))}
                      </Box>
                    ) : (
                      <TableContainer
                        component={Paper}
                        variant="outlined"
                        sx={{ borderRadius: 4, overflow: "hidden" }}
                      >
                        <Table>
                          <TableHead>
                            <TableRow sx={{ bgcolor: theme.palette.action.hover }}>
                              <TableCell>Abono</TableCell>
                              <TableCell>Monto</TableCell>
                              <TableCell>Fecha</TableCell>
                              <TableCell>Descripción</TableCell>
                              <TableCell align="center">Comprobante</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {prestamos.map((item, index) => (
                              <TableRow key={item.id} hover>
                                <TableCell>{prestamos.length - index}</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>
                                  {formatearNumero(item.cantidad)}
                                </TableCell>
                                <TableCell>{item.fecha}</TableCell>
                                <TableCell>{item.descripcion || "Sin descripción"}</TableCell>
                                <TableCell align="center">
                                  {item.comprobante ? (
                                    <IconButton
                                      onClick={() =>
                                        setComprobanteSeleccionado(item.comprobante.base64)
                                      }
                                    >
                                      <VisibilityIcon />
                                    </IconButton>
                                  ) : (
                                    <Typography variant="body2" color="text.secondary">
                                      No disponible
                                    </Typography>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </Card>
      </Container>

      <Dialog
        open={Boolean(comprobanteSeleccionado)}
        onClose={() => setComprobanteSeleccionado(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Comprobante</DialogTitle>
        <DialogContent>
          <img
            src={comprobanteSeleccionado}
            alt="comprobante"
            style={{ width: "100%", borderRadius: 8 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setComprobanteSeleccionado(null)}>Cerrar</Button>
          <Button
            onClick={() => {
              const link = document.createElement("a");
              link.href = comprobanteSeleccionado;
              link.download = "comprobante.jpg";
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }}
          >
            Descargar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ConsultaDeudor;
